<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import { useRoute } from 'vue-router'
  import {
    type Document,
    type DocumentEditTokenResponse,
    type WordDocumentTemplate,
    ResourceTypeEnum,
    DocumentsService,
    WordDocumentTemplatesService
  } from '@/types/generated'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useLocaleStore } from '@/store/localStore'

  interface Props {
    documentUuid?: string
    resourceType?: ResourceTypeEnum
    gobackUrl?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    resourceType: ResourceTypeEnum.DOCUMENT
  })

  interface OnlyOfficeConfig {
    document: {
      fileType: string
      key: string
      title: string
      url: string
      permissions: {
        edit: boolean
        download: boolean
        print: boolean
        review: boolean
        chat: boolean
        comments: boolean
      }
    }
    documentType: string
    editorConfig: {
      mode: string
      lang: string
      callbackUrl: string
      user: {
        id: string
        name: string
      }
      customization: {
        goback?: {
          url: string
        }
        autosave: boolean
        compactHeader: boolean
        compactToolbar: boolean
        compatibleFeatures: boolean
        customer: {
          address: string
          info: string
          logo: string
          logoDark: string
          mail: string
          name: string
          phone: string
          www: string
        }
        features: {
          featureTips: boolean
          roles: boolean
          feedback: boolean
          tabStyle: 'line' | 'fill'
          tabBackground: 'toolbar' | 'header'
        }
        integrationMode: 'embed'
        forcesave: boolean
        notes: boolean
        hideRightMenu: boolean
        help: boolean
        plugins: boolean
        uiTheme: string
      }
    }
    height: string
    width: string
    token: string
    events: {
      onError: (event: any) => void
      onDocumentReady: () => void
      onInfo: (event: any) => void
    }
  }

  interface OnlyOfficeEditor {
    destroyEditor: () => void
  }

  declare global {
    interface Window {
      DocsAPI: {
        DocEditor: new (elementId: string, config: OnlyOfficeConfig) => OnlyOfficeEditor
      }
      docEditor: OnlyOfficeEditor | null
    }
  }

  const route = useRoute()
  const userStore = useUserStore()
  const localeStore = useLocaleStore()
  const hasNavbar = ref(false)
  const hasSidebar = ref(false)
  const documentData = ref<Document | null>(null)
  const templateData = ref<WordDocumentTemplate | null>(null)
  const editToken = ref<string | null>(null)
  const tokenExpiresAt = ref<string | null>(null)
  const onlyofficeJwt = ref<string | null>(null)
  const tokenRefreshTimer = ref<NodeJS.Timeout | null>(null)
  const isRefreshingToken = ref(false)
  const isLoading = ref(true)
  const navbarChecker = ref<NodeJS.Timeout | null>(null)

  const documentUuid = computed(() => props.documentUuid || (route.params.documentUuid as string))

  const isTokenValid = computed(() => {
    if (!editToken.value || !tokenExpiresAt.value) return false
    const now = new Date()
    const expiresAt = new Date(tokenExpiresAt.value)
    return now < expiresAt
  })

  const needsRefresh = computed(() => {
    if (!tokenExpiresAt.value) return false
    const now = new Date()
    const expiresAt = new Date(tokenExpiresAt.value)
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000)
    return fiveMinutesFromNow >= expiresAt
  })

  function checkForNavbar() {
    const navbar = document.querySelector(
      '.navbar.navbar-expand-md.navbar-light.d-print-none.bg-danger'
    )
    hasNavbar.value = !!navbar

    if (!hasNavbar.value) {
      const dangerNavbar = document.querySelector('.navbar.bg-danger')
      hasNavbar.value = !!dangerNavbar
    }

    const sidebar = document.getElementById('admin-sidebar')
    hasSidebar.value = !!sidebar
  }

  async function fetchDocument() {
    isLoading.value = true

    if (props.resourceType === ResourceTypeEnum.WORD_TEMPLATE) {
      const response = await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
        uuid: documentUuid.value
      })
      templateData.value = response as WordDocumentTemplate
    } else {
      const response = await DocumentsService.documentsRetrieve({
        uuid: documentUuid.value
      })
      documentData.value = response as Document
    }

    isLoading.value = false
  }

  async function fetchEditToken() {
    if (isRefreshingToken.value) {
      return false
    }

    try {
      isRefreshingToken.value = true
      let response: DocumentEditTokenResponse

      if (props.resourceType === ResourceTypeEnum.WORD_TEMPLATE) {
        response = await WordDocumentTemplatesService.wordDocumentTemplatesCreateEditTokenCreate({
          uuid: documentUuid.value
        })
      } else {
        response = await DocumentsService.documentsCreateEditTokenCreate({
          uuid: documentUuid.value
        })
      }

      editToken.value = response.token
      tokenExpiresAt.value = response.expires_at
      onlyofficeJwt.value = response.onlyoffice_jwt
      scheduleTokenRefresh()
      return true
    } finally {
      isRefreshingToken.value = false
    }
  }

  function scheduleTokenRefresh() {
    if (tokenRefreshTimer.value) {
      clearTimeout(tokenRefreshTimer.value)
    }

    const now = new Date()
    const expiresAt = new Date(tokenExpiresAt.value)
    const refreshAt = new Date(expiresAt.getTime() - 5 * 60 * 1000)
    const timeUntilRefresh = refreshAt.getTime() - now.getTime()

    if (timeUntilRefresh > 0) {
      tokenRefreshTimer.value = setTimeout(async () => {
        await fetchEditToken()
      }, timeUntilRefresh)
    }
  }

  async function ensureValidToken(): Promise<boolean> {
    if (isTokenValid.value && !needsRefresh.value) {
      return true
    }
    return await fetchEditToken()
  }

  async function initializeOnlyOffice() {
    await ensureValidToken()

    const script = document.createElement('script')
    script.src = '/onlyoffice/web-apps/apps/api/documents/api.js'

    script.onload = () => {
      let filename: string
      let url: string
      let title: string
      let uuid: string

      if (props.resourceType === ResourceTypeEnum.WORD_TEMPLATE) {
        const template = templateData.value!
        filename = template.name.toLowerCase().endsWith('.docx')
          ? template.name.toLowerCase()
          : `${template.name.toLowerCase()}.docx`
        url = template.docx_template || ''
        title = template.name
        uuid = template.uuid || ''
      } else {
        const doc = documentData.value!
        filename = doc.filename.toLowerCase()
        url = doc.url
        title = doc.title || doc.filename
        uuid = doc.uuid
      }

      let fileType = 'docx'
      let documentType = 'word'

      if (filename.endsWith('.docx') || filename.endsWith('.doc')) {
        fileType = filename.endsWith('.docx') ? 'docx' : 'doc'
        documentType = 'word'
      } else if (filename.endsWith('.xlsx') || filename.endsWith('.xls')) {
        fileType = filename.endsWith('.xlsx') ? 'xlsx' : 'xls'
        documentType = 'cell'
      } else if (filename.endsWith('.pptx') || filename.endsWith('.ppt')) {
        fileType = filename.endsWith('.pptx') ? 'pptx' : 'ppt'
        documentType = 'slide'
      }

      let callbackHost = window.location.origin
      if (url.includes('localhost')) {
        url = url.replace('localhost', 'caddy')
        callbackHost = 'http://app:8000'
      }
      let callbackUrl = `${callbackHost}/d/edit/${editToken.value}`

      const currentLocale = localeStore.getLocale
      const region = currentLocale === 'de' ? 'de-DE' : 'en-US'

      window.docEditor = new window.DocsAPI.DocEditor('placeholder', {
        token: onlyofficeJwt.value!,
        document: {
          fileType: fileType,
          key: uuid,
          title: title,
          url: url,
          permissions: {
            edit: true,
            download: false,
            print: false,
            review: false,
            chat: false,
            comments: false
          }
        },
        documentType: documentType,
        editorConfig: {
          mode: 'edit',
          lang: currentLocale,
          region: region,
          callbackUrl: callbackUrl,
          user: {
            id: userStore.user!.uuid,
            name: `${userStore.user!.first_name} ${userStore.user!.last_name}`
          },
          customization: {
            ...(props.gobackUrl && {
              goback: {
                url: props.gobackUrl
              }
            }),
            macros: false,
            autosave: true,
            compactHeader: true,
            compactToolbar: false,
            compatibleFeatures: true,
            customer: {
              address: 'Hohenzollernring 89-93, 50672 Köln',
              info: 'Das KI-Sekretariat für Rechtsanwälte',
              logo: '/static/images/logo.svg',
              logoDark: '/static/images/logo.svg',
              mail: 'hello@jupus.de',
              name: 'JUPUS GmbH',
              phone: '+49 32 221855439',
              www: 'jupus.de'
            },
            features: {
              featureTips: false,
              roles: false,
              feedback: false,
              tabStyle: 'line',
              tabBackground: 'toolbar'
            },
            forcesave: true,
            notes: false,
            hideRightMenu: true,
            help: false,
            integrationMode: 'embed',
            logo: {
              image: window.location.origin + '/static/images/logo.svg',
              imageDark: window.location.origin + '/static/images/logo.svg',
              imageLight: window.location.origin + '/static/images/logo.svg',
              url: '', // Empty string to make logo non-clickable
              visible: true
            },
            plugins: false,
            review: {
              hideReviewDisplay: true,
              showReviewChanges: false,
              reviewDisplay: 'markup'
            },
            toolbarHideFileName: true,
            uiTheme: 'theme-white'
          }
        },
        height: '100%',
        width: '100%',
        events: {
          onError: function (event: any) {
            console.error('OnlyOffice Error:', event)
          },
          onDocumentReady: function () {},
          onInfo: function (_event: any) {}
        }
      })
    }

    script.onerror = () => {
      console.error('Failed to load OnlyOffice API script')
    }

    document.head.appendChild(script)
  }

  function cleanup() {
    if (tokenRefreshTimer.value) {
      clearTimeout(tokenRefreshTimer.value)
    }
    if (navbarChecker.value) {
      clearInterval(navbarChecker.value)
    }
    if (window.docEditor) {
      window.docEditor.destroyEditor()
      window.docEditor = null
    }
  }

  onMounted(async () => {
    checkForNavbar()
    navbarChecker.value = setInterval(checkForNavbar, 500)
    setTimeout(() => clearInterval(navbarChecker.value), 5000)

    await Promise.all([userStore.fetchUser(), fetchDocument()])
    await fetchEditToken()
    await initializeOnlyOffice()
  })

  onUnmounted(() => {
    cleanup()
  })
</script>

<template>
  <div
    class="onlyoffice-wrapper"
    :class="{ 'with-navbar': hasNavbar, 'with-sidebar': hasSidebar }"
  >
    <div class="onlyoffice-editor-container">
      <div
        v-if="isLoading || isRefreshingToken"
        class="loading-container"
      >
        <div class="loading-spinner"></div>
        <p v-if="isLoading">Loading document...</p>
        <p v-else-if="isRefreshingToken">Refreshing access token...</p>
      </div>

      <div
        v-else
        id="placeholder"
        class="onlyoffice-editor"
      ></div>
    </div>
  </div>
</template>

<style scoped>
  .onlyoffice-wrapper {
    position: fixed;
    top: 56px;
    left: 0;
    right: 0;
    bottom: 0;
  }

  .onlyoffice-wrapper.with-navbar {
    top: calc(60px + 52px);
  }

  .onlyoffice-wrapper.with-sidebar {
    left: 260px; /* Adjust based on sidebar width */
  }

  @media (max-width: 768px) {
    .onlyoffice-wrapper.with-sidebar {
      left: 0; /* On mobile, sidebar typically overlays or is hidden */
    }
  }

  .onlyoffice-editor-container {
    overflow: hidden;
    background: white;
    width: 100%;
    height: 100%;
  }

  .onlyoffice-editor {
    width: 100%;
    height: 100%;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 2rem;
    text-align: center;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
